import { useTranslation } from 'react-i18next';
import { useCallback, useContext, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Banner, Text } from '@figma/fpl-components';
import Divider from '@/UI/AppDivider/AppDivider';
import { Box } from '@/UI/Box/Box';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { TextSmMd, TextSmRg, Typography } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { color } from '@/colors';
import { useWorkflowStore } from '@/state/workflow.state';
import Counter from '@/UI/Counter/Counter';
import { CreditsContext } from '@/services/CreditsContext';
import { CaretIcon } from '@/UI/Icons';
import { usePrecedingIterators, type PrecedingIteratorData } from '@/hooks/usePrecedingIterators';
import { useGetCost } from '@/hooks/useGetCost';
import { PriceInfo } from '@/components/Common/PriceInfo/PriceInfo';
import { UNSUPPORTED_NODE_TYPES } from '@/consts/node-types.consts';
import { useEmptyIteratorValidation } from '@/hooks/useEmptyIteratorValidation';
import {
  FF_BLOCK_FREE_USER_MODEL_IMPORT,
  FF_BLOCK_FREE_USER_MODELS,
  FF_BLOCK_FREE_USER_VIDEO_MODEL,
  FF_MATRIX_ITERATION_MODE,
} from '@/consts/featureFlags';
import { isFreePlanBlockedModelNode } from '@/utils/free-plan-blocked-models.utils';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { useUserStore } from '@/state/user.state';
import { SubscriptionType } from '@/types/shared';
import { getImportedModelNodeInfo } from '@/utils/model-import.utils';
import { isVideoOutputNode } from '@/utils/model-video.utils';
import { isModelNode } from '@/components/Nodes/utils/nodeUtils';
import LowCreditsMsg from '@/components/Nodes/ModelComponents/ModelNodeFooter/LowCreditsMsg';
import FigmaEmbedOutOfCreditsMsg from '@/components/Nodes/ModelComponents/ModelNodeFooter/FigmaEmbedOutOfCreditsMsg';
import { FigmaEmbedRunBlockCreditsCta } from '@/components/Recipe/FlowComponents/LeftPanelSections/FigmaEmbedAiBalanceMenu';
import { ErrorMessage } from '@/components/Nodes/Shared/ErrorMessage/ErrorMessage';
import { useCreditLimitWindows } from '@/queries/useCreditLimitWindows';
import { getBlockingCreditWindow, useFigmaEmbedCreditGate } from '@/utils/credits';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { IterationModePicker } from '@/components/Recipe/FlowComponents/Iterators/IterationModePicker';
import { useIterationModeState } from '@/components/Recipe/FlowComponents/Iterators/useIterationModeState';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useApprovalGate } from '@/components/Nodes/ModelComponents/useApprovalGate';
import { PricingPageEntrypoint } from '@/enums/pricing-page.enum';
import { useFlowView } from '../FlowContext';
import { IteratorSection } from '../FlowComponents/Iterators/IteratorSection';
import { useGetActiveBatches } from '../RunFlow/batches.store';
import { useFlowRunContext } from '../RunFlow/FlowRunContext';
import { RunSelectedButton } from './RunSelectedButton';
import type { ModelCostQuote } from '@/hooks/useGetCost';
import type { DisplayedCost } from '@/components/Recipe/RunFlow/FlowRunContext';
import type { IteratorRunMode, Node } from '@/types/node';

const getNodeName = (node: Node) => {
  const name = node.data.name;
  const displayName = 'menu' in node.data ? (node.data.menu as { displayName: string })?.displayName : undefined;
  const modelName = 'model' in node.data ? (node.data.model as { name: string })?.name : undefined;
  // Prefer display name, then node name, then model name
  return displayName || name || modelName;
};

const useRunModelsButton = ({ runs, selectedNodes }: { runs: number; selectedNodes: Node[] }) => {
  const { runNodes, initializingBatchNodes, getBatchCost } = useFlowRunContext();
  const recipeId = useWorkflowStore((state) => state.recipe.id);
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>();
  const activeBatches = useGetActiveBatches(recipeId);

  const { disabled, isProcessing, disabledInfo } = useMemo(() => {
    let disabled = false;

    const selectedUnsupportedNodes = selectedNodes.filter(
      (node) => !!node.type && UNSUPPORTED_NODE_TYPES.includes(node.type as (typeof UNSUPPORTED_NODE_TYPES)[number]),
    );

    const runningNodeIds = new Set<string>();
    activeBatches.forEach((batch) => {
      batch.recipeRuns.forEach((recipeRun) => {
        recipeRun.nodeRuns.forEach((nodeRun) => {
          if (nodeRun.status === 'RUNNING' || nodeRun.status === 'PENDING') {
            runningNodeIds.add(nodeRun.nodeId);
          }
        });
      });
    });
    const initializingNodeIds = Object.values(initializingBatchNodes).flat();
    initializingNodeIds.forEach((nodeId) => {
      runningNodeIds.add(nodeId);
    });

    const hasIntersectingNodes = selectedNodes.some((sn) => runningNodeIds.has(sn.id));

    let disabledInfo: string | undefined;

    if (selectedUnsupportedNodes.length > 0) {
      const nodeNamesSet = new Set(selectedUnsupportedNodes.map(getNodeName));
      disabledInfo = t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.UNSUPPORTED_NODES_INFO, {
        count: nodeNamesSet.size,
        nodes: Array.from(nodeNamesSet).join(', '),
      });
      disabled = true;
    }

    if (hasIntersectingNodes) {
      disabledInfo = t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.ALREADY_RUNNING_INFO);
      disabled = true;
    }

    return {
      disabled,
      isProcessing: hasIntersectingNodes,
      disabledInfo,
    };
  }, [activeBatches, initializingBatchNodes, selectedNodes, t]);

  const run = useCallback(
    async (runMode?: IteratorRunMode, displayedCost?: DisplayedCost) => {
      setErrorMessage(undefined);
      try {
        await runNodes(selectedNodes, runs, runMode, displayedCost);
      } catch (error) {
        setErrorMessage(
          (error instanceof AxiosError && (error as AxiosError<{ message: string }>)?.response?.data.message) ||
            (error instanceof Error && error.message) ||
            t(I18N_KEYS.GENERAL.UNKNOWN_ERROR),
        );
      }
    },
    [runNodes, selectedNodes, runs, t],
  );

  const calculateCost = useCallback(
    async (runMode?: IteratorRunMode) => {
      return await getBatchCost(selectedNodes, runs, runMode);
    },
    [getBatchCost, selectedNodes, runs],
  );

  return useMemo(
    () => ({
      disabled,
      isProcessing,
      run,
      disabledInfo,
      errorMessage,
      setErrorMessage,
      calculateCost,
    }),
    [disabled, isProcessing, run, disabledInfo, errorMessage, calculateCost],
  );
};
const MIN_RUNS = 1;
const MAX_RUNS = 10;

/**
 * Row pairing the "Runs" label with the run-count stepper.
 *
 * Both flag branches use {@link Counter}: a stepper is the only control that supports "add one more
 * run" in a single click, and it is what the design specifies. An earlier `fpl-migration` build
 * swapped in a scrolling 1-10 `Dropdown`, which removed single-step increment and made the value a
 * scroll target (WEA-6489).
 */
export const RunCounter = ({
  runs,
  setRuns,
  isProcessing,
}: {
  runs: number;
  setRuns: (runs: number) => void;
  isProcessing: boolean;
}) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const label = t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.RUNS);

  return (
    <FlexCenVer sx={{ gap: 1, justifyContent: 'space-between', width: isFplMigrationEnabled ? '100%' : undefined }}>
      {isFplMigrationEnabled ? (
        <Text.span color={isProcessing ? 'disabled' : 'secondary'} variant="body-medium">
          {label}
        </Text.span>
      ) : (
        <TextSmRg>{label}</TextSmRg>
      )}
      <Counter count={runs} onChange={setRuns} min={MIN_RUNS} max={MAX_RUNS} disabled={isProcessing} size="small" />
    </FlexCenVer>
  );
};

const IteratorsInfo = ({ precedingIterators }: { precedingIterators: PrecedingIteratorData[] }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  return (
    <FlexCol sx={{ gap: 1 }}>
      <FlexCenVer sx={{ gap: 0.5, justifyContent: 'space-between' }} onClick={() => setIsOpen((current) => !current)}>
        <TextSmRg>{t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.ITERATORS_INFO.TITLE)}</TextSmRg>
        <CaretIcon
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'none',
            color: color.White64_T,
          }}
        />
      </FlexCenVer>

      <IteratorSection iteratorsData={precedingIterators} isOpen={isOpen} />
    </FlexCol>
  );
};

export const RunModelsErrorMessage = ({ errorMessage }: { errorMessage: string }) => {
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (isFplMigrationEnabled) {
    return (
      <Banner.InsetModal variant="danger">
        <Banner.Message>{errorMessage}</Banner.Message>
      </Banner.InsetModal>
    );
  }

  return <ErrorMessage errorMessage={errorMessage} isSmallIcon />;
};

interface RunModelsSectionProps {
  selectedNodes: Node[];
  modelCostQuotes?: ReadonlyMap<string, ModelCostQuote>;
}

export const RunModelsSection = ({ selectedNodes, modelCostQuotes }: RunModelsSectionProps) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [runs, setRuns] = useState(1);
  const { value: isMatrixIterationModeEnabled } = useFeatureFlag(FF_MATRIX_ITERATION_MODE);
  const selectedNodeIds = useMemo(() => selectedNodes.map((node) => node.id), [selectedNodes]);
  const precedingIterators = usePrecedingIterators(selectedNodeIds);

  const { isProcessing, run, disabled, disabledInfo, errorMessage, setErrorMessage, calculateCost } =
    useRunModelsButton({
      runs,
      selectedNodes,
    });

  const { approvalDisabled, approvalDisabledInfo } = useApprovalGate(selectedNodes);
  const { credits } = useContext(CreditsContext);

  // Imported and video models are a paid-plan feature: block running a selection that contains one on
  // Free plans (the server enforces the same block).
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const isFreePlan = isSubscriptionTypeIn([SubscriptionType.Free]);
  const { value: isBlockFreeUserModelImportEnabled } = useFeatureFlag(FF_BLOCK_FREE_USER_MODEL_IMPORT);
  const { value: isBlockFreeUserVideoModelEnabled } = useFeatureFlag(FF_BLOCK_FREE_USER_VIDEO_MODEL);
  const hasActiveWeaveMcpPromoCredits = useUserStore(
    (state) => state.user?.activeWorkspace?.hasActiveWeaveMcpPromoCredits === true,
  );
  const flowView = useFlowView();
  const hasBlockedImportedModel = useMemo(
    () =>
      !!isBlockFreeUserModelImportEnabled &&
      isFreePlan &&
      selectedNodes.some((node) => getImportedModelNodeInfo(node.data).isImportedModel),
    [isBlockFreeUserModelImportEnabled, isFreePlan, selectedNodes],
  );
  const hasBlockedVideoModel = useMemo(
    () =>
      !!isBlockFreeUserVideoModelEnabled &&
      !hasActiveWeaveMcpPromoCredits &&
      isFreePlan &&
      selectedNodes.some((node) => isModelNode(node) && isVideoOutputNode(node.id, flowView)),
    [isBlockFreeUserVideoModelEnabled, hasActiveWeaveMcpPromoCredits, isFreePlan, selectedNodes, flowView],
  );
  const { value: isBlockFreeUserModelsEnabled } = useFeatureFlag(FF_BLOCK_FREE_USER_MODELS);
  const hasBlockedListedModel = useMemo(
    () =>
      !!isBlockFreeUserModelsEnabled &&
      isFreePlan &&
      selectedNodes.some((node) => isModelNode(node) && isFreePlanBlockedModelNode(node.data)),
    [isBlockFreeUserModelsEnabled, isFreePlan, selectedNodes],
  );
  const paidPlanModelBlockedInfo = hasBlockedImportedModel
    ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.IMPORTED_MODEL_PAID_PLAN)
    : hasBlockedVideoModel
      ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.VIDEO_MODEL_PAID_PLAN)
      : hasBlockedListedModel
        ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.BLOCKED_MODEL_PAID_PLAN)
        : undefined;

  const {
    isRunDisabled: isIteratorRunDisabled,
    showWarning: showEmptyIteratorMsg,
    blockRunIfEmpty,
  } = useEmptyIteratorValidation({
    // Only a lone node: with more selected, the other nodes can still produce output.
    nodeId: selectedNodes.length === 1 ? selectedNodes[0].id : null,
    precedingIterators,
    isRunActive: isProcessing,
  });
  const emptyIteratorMsg = t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.EMPTY_ITERATOR);

  const { openConfirmationDialog } = useConfirmationDialogStore();

  const {
    canSelectIterationMode,
    getLatestRunMode,
    handleRunModeChange,
    runMode,
    totalRuns: iteratorTotalRuns,
  } = useIterationModeState({
    enabled: !!isMatrixIterationModeEnabled,
    nodeIds: selectedNodeIds,
  });

  const totalRuns = useMemo(() => iteratorTotalRuns * runs, [iteratorTotalRuns, runs]);

  const requestRunMode = isMatrixIterationModeEnabled ? runMode : undefined;

  const getRequestRunMode = useCallback(
    () => (isMatrixIterationModeEnabled ? getLatestRunMode() : undefined),
    [getLatestRunMode, isMatrixIterationModeEnabled],
  );

  const calculateCostForRunMode = useCallback(() => calculateCost(requestRunMode), [calculateCost, requestRunMode]);

  const { cost, isLoading, isDynamicCost, perInferenceCost, fullPerInferenceCost, promo, perSecondRate } = useGetCost({
    precedingIterators,
    calculateCost: calculateCostForRunMode,
    runMode: requestRunMode,
    selectedNodes,
    runs,
    modelCostQuotes,
  });

  const notEnoughCredits = typeof credits === 'number' && typeof cost === 'number' && credits < cost;

  // Preemptive EAP block: if any credit-limit window (daily/weekly/monthly) doesn't
  // have enough remaining for this run's cost, disable Run + show the beta banner.
  const { data: creditLimitWindows } = useCreditLimitWindows();
  const blockingCreditWindow = useMemo(
    () => getBlockingCreditWindow(creditLimitWindows?.windows, cost),
    [creditLimitWindows, cost],
  );

  const { gateOnFigmaMeter, figmaMeterStatus, effectiveBlockingWindow, creditsBlocked } = useFigmaEmbedCreditGate(
    blockingCreditWindow,
    notEnoughCredits,
  );

  const handleRun = useCallback(async () => {
    if (blockRunIfEmpty()) {
      setErrorMessage(undefined);
      return;
    }
    const latestRunMode = getRequestRunMode();
    const displayedCost: DisplayedCost = { credits: perInferenceCost, perSecondRate, surface: 'drawer' };
    if (isDynamicCost) {
      return openConfirmationDialog({
        title: t(I18N_KEYS.RECIPE_MAIN.FLOW.UNKNOWN_COST.DIALOG.TITLE),
        message: t(I18N_KEYS.RECIPE_MAIN.FLOW.UNKNOWN_COST.DIALOG.MSG),
        confirmBtnText: t(I18N_KEYS.RECIPE_MAIN.FLOW.UNKNOWN_COST.DIALOG.CONFIRM_BTN_TEXT),
        width: 'md',
        onConfirm: () => void run(getRequestRunMode(), displayedCost),
      });
    }
    await run(latestRunMode, displayedCost);
  }, [
    blockRunIfEmpty,
    setErrorMessage,
    getRequestRunMode,
    isDynamicCost,
    openConfirmationDialog,
    perInferenceCost,
    perSecondRate,
    run,
    t,
  ]);

  const iteratorControls = precedingIterators?.length ? (
    <>
      <IteratorsInfo precedingIterators={precedingIterators} />
      {canSelectIterationMode ? (
        <FlexCol sx={{ gap: 1.5 }}>
          <IterationModePicker mode={runMode} onChange={handleRunModeChange} disabled={isProcessing} variant="panel" />
          <FlexCenVer sx={{ justifyContent: 'space-between' }}>
            {isFplMigrationEnabled ? (
              <Text.span color="secondary" variant="body-medium">
                {t(I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_TOTAL_RUNS)}
              </Text.span>
            ) : (
              <TextSmRg sx={{ color: color.White80_T }}>
                {t(I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_TOTAL_RUNS)}
              </TextSmRg>
            )}
            <TextSmMd>{totalRuns}</TextSmMd>
          </FlexCenVer>
        </FlexCol>
      ) : null}
      <Divider orientation="horizontal" flexItem sx={{ backgroundColor: color.White08_T }} />
    </>
  ) : null;
  const priceInfo = (
    <PriceInfo
      longTitle
      cost={perInferenceCost}
      fullCost={fullPerInferenceCost}
      promo={promo}
      perSecondRate={perSecondRate}
      isLoading={isLoading}
      isDynamicCost={isDynamicCost}
      showBetaLabel
    />
  );
  const runButton = (
    <Tooltip
      classes={{ tooltip: 'notranslate' }}
      title={
        approvalDisabledInfo ??
        paidPlanModelBlockedInfo ??
        disabledInfo ??
        (isIteratorRunDisabled ? emptyIteratorMsg : undefined)
      }
      enterDelay={1000}
      enterNextDelay={1000}
    >
      <Box sx={{ width: '100%', height: '24px' }}>
        <RunSelectedButton
          onClick={handleRun}
          disabled={
            disabled ||
            (isLoading && !!modelCostQuotes?.size) ||
            creditsBlocked ||
            approvalDisabled ||
            hasBlockedImportedModel ||
            hasBlockedVideoModel ||
            hasBlockedListedModel ||
            isIteratorRunDisabled ||
            !!effectiveBlockingWindow
          }
          sx={{ width: '100%' }}
        />
      </Box>
    </Tooltip>
  );
  const statusMessages = (
    <>
      {errorMessage && !effectiveBlockingWindow && !figmaMeterStatus.blocked && (
        <RunModelsErrorMessage errorMessage={errorMessage} />
      )}
      {showEmptyIteratorMsg && <RunModelsErrorMessage errorMessage={emptyIteratorMsg} />}
      {effectiveBlockingWindow ? (
        <FigmaEmbedOutOfCreditsMsg status={effectiveBlockingWindow} />
      ) : figmaMeterStatus.blocked ? (
        <FigmaEmbedOutOfCreditsMsg status={figmaMeterStatus} cta={<FigmaEmbedRunBlockCreditsCta />} />
      ) : (
        !gateOnFigmaMeter &&
        notEnoughCredits &&
        !isProcessing && <LowCreditsMsg analyticsContext={{ entrypoint: PricingPageEntrypoint.LowCredits }} />
      )}
    </>
  );
  const hasStatusMessage =
    (!!errorMessage && !effectiveBlockingWindow && !figmaMeterStatus.blocked) ||
    showEmptyIteratorMsg ||
    !!effectiveBlockingWindow ||
    figmaMeterStatus.blocked ||
    (!gateOnFigmaMeter && notEnoughCredits && !isProcessing);

  if (isFplMigrationEnabled) {
    return (
      <FlexCol sx={{ px: 2, pb: 1.5 }}>
        <FlexCenVer sx={{ height: 40 }}>
          <Text.span color="default" truncate variant="body-medium-strong">
            {t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.TITLE)}
          </Text.span>
        </FlexCenVer>
        <FlexCenVer sx={{ height: 32 }}>
          <RunCounter runs={runs} setRuns={setRuns} isProcessing={isProcessing} />
        </FlexCenVer>
        {iteratorControls ? <FlexCol sx={{ py: 0.5, gap: 1.5 }}>{iteratorControls}</FlexCol> : null}
        <FlexCenVer sx={{ height: 32 }}>{priceInfo}</FlexCenVer>
        {hasStatusMessage ? <FlexCol sx={{ py: 0.5, gap: 1.5 }}>{statusMessages}</FlexCol> : null}
        <FlexCenVer sx={{ height: 32 }}>{runButton}</FlexCenVer>
      </FlexCol>
    );
  }

  return (
    <FlexCol sx={{ p: 2, gap: 2 }}>
      <Typography variant="label-xs-rg" sx={{ color: color.White64_T }}>
        {t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.TITLE)}
      </Typography>
      <FlexCol sx={{ gap: 1.5 }}>
        <RunCounter runs={runs} setRuns={setRuns} isProcessing={isProcessing} />
        {iteratorControls}
        {priceInfo}
        {runButton}
        {statusMessages}
      </FlexCol>
    </FlexCol>
  );
};
